// ============================================================
// useDirection - the session phase, for every member.
//
// One listener on `direction/frame`. The table's phase is one of
// three states, and the difference between the first two is the point:
//
//   null (phase)  - NOT KNOWN YET. The shell draws no layout, so a
//                   reload mid-Stealth never flashes Combat first.
//   a phase       - the stored one, or Combat once the server has said
//                   there is no frame (an old session), or after
//                   UNKNOWN_CEILING_MS with no answer at all.
//
// The GM's `setPhase` writes it for the table. `look` is the GM's
// PEEK: a local phase that moves nobody else. `shown` is what this
// screen lays out - the peek when there is one, the table's otherwise.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { emptyFrame, readFrame, withPhase } from '../engine/phase/frame';
import type { Phase, PhaseFrame } from '../engine/phase/types';

/** How long an unanswered read may hold the layout back before the table is taken to be in Combat. */
export const UNKNOWN_CEILING_MS = 1500;

interface DirectionSource {
  onFrameUpdate(sessionId: string, callback: (frame: unknown | null) => void): () => void;
  setFrame(sessionId: string, frame: PhaseFrame): Promise<void>;
}

interface Params {
  sessionId: string | null | undefined;
  mp: DirectionSource;
}

/** What the GM sets on the frame besides the phase. */
type Directions = Partial<Pick<PhaseFrame, 'partyTeam' | 'dangerPin' | 'positions' | 'beats' | 'stealth' | 'timer'>>;

export interface Direction {
  /** The whole frame; null while it is not known. */
  frame: PhaseFrame | null;
  /** The table's phase; null while it is not known. */
  phase: Phase | null;
  /** What this screen lays out; null while the table's phase is not known. */
  shown: Phase | null;
  /** True while this viewer looks at a phase without moving the table. */
  peeking: boolean;
  /** Move the whole table. Refused (false) while the phase is not known: a default is never written. */
  setPhase(phase: Phase): boolean;
  /** Look at a phase locally; null stops looking. */
  look(phase: Phase | null): void;
  /** Name the party's team or pin the danger, for the whole table. Refused (false) while the frame is not known. */
  direct(patch: Directions): boolean;
  /** Put a whole frame back (an undo). Validated like a stored one; writes nothing when it is what stands. */
  impose(frame: unknown): boolean;
}

/** `undefined` not known yet, otherwise the frame (the default one when the server holds none). */
type FrameState = PhaseFrame | undefined;

/** Subscribe, ignoring anything a torn-down listener still delivers (see `listenLive` in usePermissions). */
/** Writes of the frame sent from this screen and not yet answered. */
type Writing = { current: number };

function followFrame(
  mp: DirectionSource,
  sessionId: string,
  set: (next: FrameState) => void,
  settle: () => void,
  writing: Writing,
) {
  let live = true;
  const unsub = mp.onFrameUpdate(sessionId, (doc) => {
    // A READ THAT ARRIVES WHILE MY OWN WRITE IS IN FLIGHT IS THE FRAME AS IT WAS. Taking it would
    // put the old frame back on screen, and the next change (built on what is on screen) would
    // then quietly undo the one in flight for the whole table. The write's own echo follows.
    if (live && writing.current === 0) set(doc === null ? emptyFrame() : readFrame(doc));
  });
  const ceiling = setTimeout(settle, UNKNOWN_CEILING_MS);
  return () => {
    live = false;
    clearTimeout(ceiling);
    unsub();
  };
}

/** The frame as the session holds it: local outside a session, followed inside one. */
function useFrame(sessionId: Params['sessionId'], mp: DirectionSource, writing: Writing) {
  // Outside a session there is nothing to wait for.
  const [frame, setFrame] = useState<FrameState>(() => (sessionId ? undefined : emptyFrame()));
  useEffect(() => {
    if (!sessionId) return undefined;
    const settle = () => setFrame((known) => known ?? emptyFrame());
    return followFrame(mp, sessionId, setFrame, settle, writing);
  }, [sessionId, mp, writing]);
  return [frame, setFrame] as const;
}

/** Apply a change to the frame, here at once and on the wire. Answers false while the frame is not known. */
function useFrameWriter(
  sessionId: Params['sessionId'],
  mp: DirectionSource,
  frame: FrameState,
  setFrame: (f: PhaseFrame) => void,
  writing: Writing,
) {
  const frameRef = useRef(frame);
  frameRef.current = frame;
  return useCallback(
    (change: (current: PhaseFrame) => PhaseFrame) => {
      const current = frameRef.current;
      if (!current) return false;
      const next = change(current);
      // At once, not at the next render: a second change in the same tick must build on this one.
      frameRef.current = next;
      setFrame(next);
      if (!sessionId) return true;
      writing.current += 1;
      mp.setFrame(sessionId, next)
        .catch(console.error)
        .finally(() => {
          writing.current -= 1;
        });
      return true;
    },
    [sessionId, mp, setFrame, writing],
  );
}

export default function useDirection({ sessionId, mp }: Params): Direction {
  const writing = useRef(0);
  const [frame, setFrame] = useFrame(sessionId, mp, writing);
  const [peek, setPeek] = useState<Phase | null>(null);
  const write = useFrameWriter(sessionId, mp, frame, setFrame, writing);

  const setPhase = useCallback(
    (phase: Phase) => {
      const moved = write((current) => withPhase(current, phase));
      if (moved) setPeek(null);
      return moved;
    },
    [write],
  );
  const direct = useCallback((patch: Directions) => write((current) => ({ ...current, ...patch })), [write]);
  const impose = useCallback(
    (stored: unknown) => {
      const next = readFrame(stored);
      const same = JSON.stringify(next) === JSON.stringify(frame);
      return same || write(() => next);
    },
    [write, frame],
  );

  const phase = frame?.phase ?? null;
  const shown = phase && (peek ?? phase);
  return { frame: frame ?? null, phase, shown, peeking: peek !== null, setPhase, look: setPeek, direct, impose };
}
